import { toLatinDigits } from "./national-id.js";
/**
 * Whether a string could be an Iranian mobile number, and its canonical form.
 *
 * In `@talent/protocol` for the same reason `national-id.ts` is: the server must refuse a
 * malformed number, and the sign-in and registration forms should say so before a code is spent
 * on a number that could never receive it. One definition of the rule, used on both sides.
 *
 * `services/api/src/domain/phone.ts` builds the operator-only shapes (the masked form the panel
 * shows, the local `09…` form the SMS provider wants) on top of this. Those stay there — nothing
 * on the client masks a number for an operator's screen or talks to the SMS provider directly.
 *
 * Canonical form is `+989XXXXXXXXX`. The account identifier has to be canonical or the same
 * person gets two accounts by typing `0912…` on signup and `+98912…` on login — and then loses
 * access to their child's history, which is unrecoverable from their point of view even though
 * nothing was deleted.
 */
/**
 * Strips whichever international prefix was used, then the trunk zero, if either is there.
 *
 * Exported for `cleanMobileFragment` in `services/api/src/domain/phone.ts`, which needs the same
 * prefix-stripping for a search fragment that is never a complete number — duplicating it there
 * would be a second definition of what a prefix is.
 */
export function stripKnownPrefix(digits) {
    let rest = digits;
    if (rest.startsWith("+98"))
        rest = rest.slice(3);
    else if (rest.startsWith("0098"))
        rest = rest.slice(4);
    else if (rest.startsWith("98") && rest.length === 12)
        rest = rest.slice(2);
    if (rest.startsWith("0"))
        rest = rest.slice(1);
    return rest;
}
/**
 * Returns the canonical form, or null if this cannot be an Iranian mobile number.
 *
 * Accepted inputs, all of which real users type: `09123456789`, `9123456789`,
 * `+989123456789`, `00989123456789`, and any of those with spaces or dashes.
 */
export function normalizeIranianMobile(input) {
    const rest = stripKnownPrefix(toLatinDigits(input).replace(/[\s()-]/g, ""));
    // Iranian mobile numbers are 9 followed by nine more digits. The leading 9 is checked
    // because a landline reaching this point would otherwise be stored as an account
    // identifier that can never receive the OTP this will eventually use.
    if (!/^9\d{9}$/.test(rest))
        return null;
    return `+98${rest}`;
}
/**
 * Whether a string is a real Iranian mobile number, for a submit button's `disabled` prop —
 * which wants a boolean, not the canonical form `normalizeIranianMobile` returns.
 */
export function isValidIranianMobile(input) {
    return normalizeIranianMobile(input) !== null;
}
//# sourceMappingURL=phone.js.map